import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../../helpers/lpApi';

// ============================================================================
// Big Projects · Manage Company — full company-management console for Large
// Projects. Unlike "Company Providers" (which only surfaces the PENDING KYB
// queue + pending docs), this page lists ALL CompanyProfile rows in every KYB
// status, with a status filter + search, and per-row management actions.
//
// Companies are governed by KYB + license + insurance. There is intentionally
// NO background-check column here — background checks are the per-worker $49
// flow, which does not apply to companies.
//
// Backed by besthand-api's `adminListCompanies` resolver via the `/lp-graphql`
// proxy (lpApi). All actions REUSE existing besthand-api mutations — no KYB or
// verification logic is duplicated in besthand-web (architecture decision).
// ============================================================================

const Q_COMPANIES = `
  query ($status: String, $search: String, $limit: Int, $offset: Int) {
    adminListCompanies(status: $status, search: $search, limit: $limit, offset: $offset) {
      status errorMessage
      companyProfiles {
        id ownerUserId ownerIsBan
        legalName dbaName einLast4
        kybStatus kybStatusReason kybReviewedAt kybReviewedBy
        operatingStates specialties serviceRadiusMiles
        headquartersAddressLine1 headquartersAddressLine2 headquartersCity
        headquartersState headquartersZip headquartersCountry
        headquartersLat headquartersLng
        repFirstName repLastName repEmail repPhone
        repAddressLine1 repAddressCity repAddressState repAddressPostalCode repAddressCountry
        middeskBusinessId middeskNameStatus middeskTinStatus middeskSosStatus
        middeskWatchlistStatus middeskOverallStatus middeskVerifiedAt
        stripeKybVerified stripeKybPayoutsEnabled stripeKybCheckedAt
        stripeConnectAccountId stripeConnectTosAcceptedAt
        publicVisibility publicSlug websiteUrl logoUrl tagline yearFounded
        referralCode
        createdAt updatedAt
        owner { userId firstName lastName email phone state city zipcode isBan }
        licenses {
          id type number state classification issuingBody expirationDate
          verificationStatus documentUrl
        }
        insurances {
          id type carrier policyNumber coverageAmount currency expirationDate
          verificationStatus coiDocumentUrl
        }
      }
    }
  }
`;

const M_APPROVE = `
  mutation ($companyId: String!, $notes: String, $force: Boolean) {
    approveCompanyKyb(companyId: $companyId, notes: $notes, force: $force) {
      status errorMessage companyProfile { id kybStatus }
    }
  }
`;

const M_REJECT = `
  mutation ($companyId: String!, $reason: String!) {
    rejectCompanyKyb(companyId: $companyId, reason: $reason) {
      status errorMessage companyProfile { id kybStatus kybStatusReason }
    }
  }
`;

// KYB — Middesk Business Verification auto-run. ENV-GATED on the besthand-api
// side (MIDDESK_API_KEY): when Middesk is not configured the resolver returns a
// clean "Middesk not configured." status 200 and nothing changes — the button
// degrades gracefully. On a CLEAR PASS the api may auto-promote KYB to APPROVED.
const M_MIDDESK = `
  mutation ($companyId: String!) {
    runCompanyKybAutoVerify(companyId: $companyId) {
      status errorMessage
      companyProfile {
        id kybStatus kybReviewedBy
        middeskBusinessId middeskNameStatus middeskTinStatus middeskSosStatus
        middeskWatchlistStatus middeskOverallStatus middeskVerifiedAt
      }
    }
  }
`;

// KYB — Stripe Connect verification. Reads the LIVE Stripe account state on the
// besthand-api side (read-only stripe.accounts.retrieve). On a verified result
// (payouts_enabled && no disabled_reason) the api auto-promotes KYB to APPROVED
// and persists the snapshot. Additive to the manual + Middesk paths. When the
// company has no Stripe account / Stripe is unconfigured the resolver returns a
// clean status 200 and nothing changes — the button degrades gracefully.
const M_STRIPE = `
  mutation ($companyId: String!) {
    runCompanyKybViaStripe(companyId: $companyId) {
      status errorMessage
      companyProfile {
        id kybStatus kybReviewedBy
        stripeKybVerified stripeKybPayoutsEnabled stripeKybCheckedAt
      }
    }
  }
`;

const M_VERIFY_LICENSE = `
  mutation ($licenseId: String!, $decision: String!, $notes: String) {
    verifyLicense(licenseId: $licenseId, decision: $decision, notes: $notes) {
      status errorMessage license { id verificationStatus }
    }
  }
`;

const M_VERIFY_INSURANCE = `
  mutation ($insuranceId: String!, $decision: String!, $notes: String) {
    verifyInsurance(insuranceId: $insuranceId, decision: $decision, notes: $notes) {
      status errorMessage insurance { id verificationStatus }
    }
  }
`;

// General admin suspend / reinstate primitive — flips the owner's User.isBan.
const M_SUSPEND_USER = `
  mutation ($userId: String!, $suspend: Boolean!, $reason: String) {
    suspendUser(userId: $userId, suspend: $suspend, reason: $reason) {
      status errorMessage userId isBan suspendedReason suspendedAt
    }
  }
`;

const STATUSES = ['ALL', 'DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'SUSPENDED'];

const S = {
  page: { maxWidth: 1320, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' },
  pill: (on) => ({ padding: '7px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 13, border: '1px solid #e7e9ec', borderRadius: 999, background: on ? '#e8232b' : '#fff', color: on ? '#fff' : '#5a6168' }),
  input: { padding: '9px 12px', fontSize: 14, border: '1px solid #e7e9ec', borderRadius: 9, minWidth: 240, outline: 'none' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '18px 20px', marginBottom: 16, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  logoBox: { display: 'flex', gap: 14, alignItems: 'center' },
  logo: { width: 44, height: 44, borderRadius: 10, objectFit: 'cover', border: '1px solid #eef2f4', background: '#f6f8fa' },
  name: { fontSize: 18, fontWeight: 800, margin: 0 },
  tagline: { color: '#6b7280', fontSize: 13, margin: '2px 0 0' },
  meta: { color: '#5a6168', fontSize: 13, marginTop: 6, lineHeight: 1.6 },
  k: { color: '#9aa1a8', fontWeight: 700 },
  badge: (kind) => {
    const map = {
      PENDING: { bg: '#fff3d6', fg: '#946200' },
      APPROVED: { bg: '#e7f6ec', fg: '#137a37' },
      VERIFIED: { bg: '#e7f6ec', fg: '#137a37' },
      REJECTED: { bg: '#fde7e8', fg: '#c2151c' },
      SUSPENDED: { bg: '#fde7e8', fg: '#c2151c' },
      EXPIRED: { bg: '#fde7e8', fg: '#c2151c' },
      DRAFT: { bg: '#eef2ff', fg: '#3730a3' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  section: { marginTop: 16, borderTop: '1px solid #f0f2f4', paddingTop: 14 },
  secTitle: { fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', color: '#9aa1a8', textTransform: 'uppercase', marginBottom: 10 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '6px 10px', color: '#9aa1a8', fontWeight: 700, borderBottom: '1px solid #f0f2f4', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f6f8fa', verticalAlign: 'middle' },
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  miniActions: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  btn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnSm: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  btnRed: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnRedSm: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnGhostSm: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  // Middesk auto-verify button (purple/indigo to set it apart from KYB actions)
  btnMiddesk: { background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  // Stripe verify button (teal — distinct from Middesk indigo + KYB green/red)
  btnStripe: { background: '#0e7490', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  // Middesk per-check status pill (maps Middesk success/failure/in_review/etc.)
  mdBadge: (raw) => {
    const k = String(raw || '').toLowerCase();
    let bg = '#eef2f4'; let fg = '#5a6168';
    if (k === 'success' || k === 'passed' || k === 'verified') { bg = '#e7f6ec'; fg = '#137a37'; }
    else if (k === 'failure' || k === 'failed') { bg = '#fde7e8'; fg = '#c2151c'; }
    else if (k === 'in_review' || k === 'pending' || k === 'warning') { bg = '#fff3d6'; fg = '#946200'; }
    else if (k === 'error') { bg = '#fde7e8'; fg = '#c2151c'; }
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', padding: '4px 9px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap', textTransform: 'uppercase' };
  },
  mdGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: 4 },
  mdCell: { background: '#fafbfc', border: '1px solid #eef2f4', borderRadius: 10, padding: '10px 12px' },
  // Dirigeant (company representative) block
  dirGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 },
  dirCell: { background: '#fafbfc', border: '1px solid #eef2f4', borderRadius: 10, padding: '10px 14px' },
  dirLabel: { fontSize: 10, fontWeight: 800, letterSpacing: '0.07em', color: '#9aa1a8', textTransform: 'uppercase', marginBottom: 4 },
  dirValue: { fontSize: 14, fontWeight: 700, color: '#0f1214', wordBreak: 'break-word' },
  dirName: { fontSize: 15, fontWeight: 800, color: '#0f1214', marginBottom: 12 },
  fallbackHint: { display: 'inline-block', fontSize: 10, fontWeight: 700, color: '#9aa1a8', marginLeft: 6, fontStyle: 'italic' },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  none: { color: '#9aa1a8', fontSize: 13, padding: '6px 0' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
};

const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString(); } catch (e) { return s; } };
const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toLocaleString()}`);

function ManageCompany() {
  const [companies, setCompanies] = useState([]);
  const [status, setStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const vars = { limit: 200, offset: 0 };
      if (status && status !== 'ALL') vars.status = status;
      if (search && search.trim()) vars.search = search.trim();
      const res = await lpApi(Q_COMPANIES, vars);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.adminListCompanies;
        if (env && env.status === 200) setCompanies(env.companyProfiles || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load companies: ${e.message}` });
    }
    setLoading(false);
  }, [status, search]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, query, variables, opName, okText) => {
    setBusyId(key);
    setMsg(null);
    try {
      const res = await lpApi(query, variables);
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data[opName];
      if (env && env.status >= 200 && env.status < 300) {
        setMsg({ ok: okText });
        await load();
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusyId('');
  };

  const approve = (c, force = false) => run(`c${c.id}`, M_APPROVE, { companyId: String(c.id), notes: null, force }, 'approveCompanyKyb', `${force ? 'Force-approved' : 'Approved'} ${c.legalName}.`);
  // ADMIN OVERRIDE: approve even when Stripe/license/insurance aren't verified.
  // Recorded in the audit trail + kybStatusReason. Confirm first (compliance risk).
  const forceApprove = (c) => {
    if (typeof window !== 'undefined' && !window.confirm(
      `Force-approve ${c.legalName}?\n\nThis OVERRIDES the Stripe Connect / verified license / verified insurance checks and activates the company anyway. It is recorded in the audit trail. Only do this if compliance has been verified another way.`,
    )) return;
    approve(c, true);
  };

  // KYB — run Middesk auto-verification. We surface the returned overall +
  // sub-statuses (or the "not configured" message) in the alert.
  const middesk = async (c) => {
    setBusyId(`c${c.id}`);
    setMsg(null);
    try {
      const res = await lpApi(M_MIDDESK, { companyId: String(c.id) });
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data.runCompanyKybAutoVerify;
      if (env && env.status >= 200 && env.status < 300) {
        if (env.errorMessage) {
          // e.g. "Middesk not configured." — informational, not an error.
          setMsg({ ok: `${c.legalName}: ${env.errorMessage}` });
        } else {
          const p = env.companyProfile || {};
          const parts = [
            p.middeskOverallStatus ? `Overall ${p.middeskOverallStatus}` : null,
            p.middeskNameStatus ? `Name ${p.middeskNameStatus}` : null,
            p.middeskTinStatus ? `TIN ${p.middeskTinStatus}` : null,
            p.middeskSosStatus ? `SoS ${p.middeskSosStatus}` : null,
            p.middeskWatchlistStatus ? `OFAC ${p.middeskWatchlistStatus}` : null,
          ].filter(Boolean).join(' · ');
          setMsg({ ok: `${c.legalName} verified — ${parts || 'result recorded'} (KYB: ${p.kybStatus}).` });
        }
        await load();
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Middesk verify failed: ${e.message}` });
    }
    setBusyId('');
  };

  // KYB — run Stripe Connect verification. Surfaces verified / payouts state (or
  // the "no Stripe account" message) in the alert. On verified the api promotes
  // KYB to APPROVED.
  const stripeKyb = async (c) => {
    setBusyId(`c${c.id}`);
    setMsg(null);
    try {
      const res = await lpApi(M_STRIPE, { companyId: String(c.id) });
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data.runCompanyKybViaStripe;
      if (env && env.status >= 200 && env.status < 300) {
        const p = env.companyProfile || {};
        if (p.stripeKybVerified) {
          setMsg({ ok: `${c.legalName} — Stripe verified (payouts ${p.stripeKybPayoutsEnabled ? 'enabled' : 'disabled'}); KYB: ${p.kybStatus}.` });
        } else {
          // env.errorMessage carries the reason (e.g. stripe_not_verified,
          // "No Stripe Connect account yet…", stripe_<disabled_reason>).
          setMsg({ ok: `${c.legalName} — not Stripe-verified${env.errorMessage ? ` (${env.errorMessage})` : ''}.` });
        }
        await load();
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Stripe verify failed: ${e.message}` });
    }
    setBusyId('');
  };

  const reject = (c) => {
    const reason = typeof window !== 'undefined' ? window.prompt(`Reject ${c.legalName}? Reason (shown to the company):`) : '';
    if (!reason || reason.trim().length < 3) { if (reason !== null) setMsg({ error: 'A reason of at least 3 characters is required.' }); return; }
    run(`c${c.id}`, M_REJECT, { companyId: String(c.id), reason: reason.trim() }, 'rejectCompanyKyb', `Rejected ${c.legalName}.`);
  };

  const toggleSuspend = (c) => {
    const ownerId = c.ownerUserId || (c.owner && c.owner.userId);
    const isBan = (c.owner && c.owner.isBan) || c.ownerIsBan;
    if (!ownerId) { setMsg({ error: 'No owner account on file for this company.' }); return; }
    if (!isBan) {
      const reason = typeof window !== 'undefined'
        ? window.prompt(`Suspend the owner of ${c.legalName}? Reason (optional, stored on the account):`)
        : '';
      if (reason === null) return; // Cancel
      run(`u${ownerId}`, M_SUSPEND_USER, { userId: String(ownerId), suspend: true, reason: reason.trim() || null }, 'suspendUser', `Suspended ${c.legalName}.`);
    } else {
      if (typeof window !== 'undefined' && !window.confirm(`Reinstate the owner of ${c.legalName}?`)) return;
      run(`u${ownerId}`, M_SUSPEND_USER, { userId: String(ownerId), suspend: false, reason: null }, 'suspendUser', `Reinstated ${c.legalName}.`);
    }
  };

  const verifyLic = (l, decision) => run(`l${l.id}`, M_VERIFY_LICENSE, { licenseId: String(l.id), decision, notes: null }, 'verifyLicense', `License ${decision.toLowerCase()}.`);
  const verifyIns = (i, decision) => run(`i${i.id}`, M_VERIFY_INSURANCE, { insuranceId: String(i.id), decision, notes: null }, 'verifyInsurance', `Insurance ${decision.toLowerCase()}.`);

  const submitSearch = (e) => { if (e) e.preventDefault(); setSearch(searchInput); };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Manage Company</h2>
      <div style={S.hint}>
        Full Large Projects company directory — every company across all KYB statuses, with KYB,
        license, insurance, and owner-account management. Companies are governed by KYB + license +
        insurance (no background check applies to companies).
      </div>

      <form style={S.toolbar} onSubmit={submitSearch}>
        {STATUSES.map((st) => (
          <button type="button" key={st} style={S.pill(status === st)} onClick={() => setStatus(st)}>
            {st === 'ALL' ? 'All' : st.charAt(0) + st.slice(1).toLowerCase()}
          </button>
        ))}
        <input
          style={S.input}
          placeholder="Search legal name / DBA / EIN last-4…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit" style={S.btnGhost}>Search</button>
        <button type="button" style={{ ...S.btnGhost, marginLeft: 'auto' }} onClick={() => load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </form>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && companies.length === 0 && (
        <div style={S.empty}>No companies match this filter.</div>
      )}

      {!loading && companies.map((c) => {
        const owner = c.owner || {};
        const ownerName = [owner.firstName, owner.lastName].filter(Boolean).join(' ') || '—';
        const ownerBan = owner.isBan || c.ownerIsBan;
        const hq = [
          c.headquartersAddressLine1, c.headquartersAddressLine2, c.headquartersCity,
          c.headquartersState, c.headquartersZip, c.headquartersCountry,
        ].filter(Boolean).join(', ') || 'No HQ on file';
        const licenses = c.licenses || [];
        const insurances = c.insurances || [];

        // ----- Dirigeant (company representative) ---------------------------
        // Source from the persisted rep* fields, falling back to the account
        // owner when a rep field is empty. We track per-field fallback so the
        // UI can flag "(from account owner)".
        const repName = [c.repFirstName, c.repLastName].filter(Boolean).join(' ').trim();
        const ownerNameJoined = [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim();
        const pick = (repVal, ownerVal) => {
          if (repVal != null && String(repVal).trim() !== '') return { value: String(repVal).trim(), fb: false };
          if (ownerVal != null && String(ownerVal).trim() !== '') return { value: String(ownerVal).trim(), fb: true };
          return { value: '—', fb: false };
        };
        const dir = {
          name: repName ? { value: repName, fb: false } : (ownerNameJoined ? { value: ownerNameJoined, fb: true } : { value: '—', fb: false }),
          email: pick(c.repEmail, owner.email),
          state: pick(c.repAddressState, owner.state),
          zip: pick(c.repAddressPostalCode, owner.zipcode),
          city: pick(c.repAddressCity, owner.city),
        };
        // Phone(s): show rep + owner when both exist and differ.
        const repPhone = c.repPhone && String(c.repPhone).trim();
        const ownerPhone = owner.phone && String(owner.phone).trim();
        let dirPhoneValue = '—';
        let dirPhoneFb = false;
        if (repPhone && ownerPhone && repPhone !== ownerPhone) {
          dirPhoneValue = `${repPhone} · ${ownerPhone} (owner)`;
        } else if (repPhone) {
          dirPhoneValue = repPhone;
        } else if (ownerPhone) {
          dirPhoneValue = ownerPhone;
          dirPhoneFb = true;
        }

        return (
          <div style={S.card} key={c.id}>
            <div style={S.rowTop}>
              <div style={S.logoBox}>
                {c.logoUrl ? <img style={S.logo} src={c.logoUrl} alt="" /> : <div style={S.logo} />}
                <div>
                  <p style={S.name}>
                    {c.legalName}{c.dbaName ? ` · dba ${c.dbaName}` : ''}
                    {c.yearFounded ? <span style={{ ...S.k, fontWeight: 700, fontSize: 13 }}> · est. {c.yearFounded}</span> : null}
                  </p>
                  {c.tagline ? <p style={S.tagline}>{c.tagline}</p> : null}
                  <div style={S.meta}>
                    <span style={S.k}>Company #{c.id}</span>
                    {c.einLast4 ? ` · EIN ••${c.einLast4}` : ''}
                    {` · HQ: ${hq}`}
                  </div>
                  <div style={S.meta}>
                    {(c.specialties && c.specialties.length) ? <span><span style={S.k}>Specialties:</span> {c.specialties.join(', ')} · </span> : null}
                    {(c.operatingStates && c.operatingStates.length) ? <span><span style={S.k}>States:</span> {c.operatingStates.join(', ')} · </span> : null}
                    <span style={S.k}>Radius:</span> {c.serviceRadiusMiles != null ? `${c.serviceRadiusMiles} mi` : '—'}
                  </div>
                  <div style={S.meta}>
                    <span style={S.k}>Owner:</span> {ownerName}
                    {owner.email ? ` · ${owner.email}` : ''}
                    {owner.phone ? ` · ${owner.phone}` : ''}
                    {ownerBan ? <span style={{ ...S.badge('SUSPENDED'), marginLeft: 6 }}>SUSPENDED</span> : null}
                  </div>
                  <div style={S.meta}>
                    <span style={S.k}>Referral code:</span> {c.referralCode || '—'}
                  </div>
                  <div style={S.meta}>
                    <span style={S.k}>Stripe:</span> {c.stripeConnectAccountId || '—'}
                    {c.stripeConnectTosAcceptedAt ? ` · TOS ${fmtDate(c.stripeConnectTosAcceptedAt)}` : ''}
                    {` · `}<span style={S.k}>Public:</span> {c.publicVisibility ? 'Yes' : 'No'}
                    {c.publicSlug ? ` (/${c.publicSlug})` : ''}
                    {c.websiteUrl ? <span> · <a href={c.websiteUrl} target="_blank" rel="noreferrer">{c.websiteUrl}</a></span> : null}
                  </div>
                  <div style={S.meta}>
                    {c.kybStatusReason ? <span><span style={S.k}>KYB note:</span> {c.kybStatusReason} · </span> : null}
                    <span style={S.k}>Reviewed:</span> {c.kybReviewedAt ? fmtDate(c.kybReviewedAt) : '—'}
                    {c.kybReviewedBy ? ` by ${c.kybReviewedBy}` : ''}
                    {` · `}<span style={S.k}>Created:</span> {fmtDate(c.createdAt)}
                    {` · `}<span style={S.k}>Updated:</span> {fmtDate(c.updatedAt)}
                  </div>
                </div>
              </div>
              <span style={S.badge(c.kybStatus)}>{c.kybStatus || 'DRAFT'}</span>
            </div>

            <div style={S.actions}>
              <button style={S.btn} disabled={busyId === `c${c.id}` || c.kybStatus === 'APPROVED'} onClick={() => approve(c)}>
                {busyId === `c${c.id}` ? '…' : 'Approve KYB'}
              </button>
              <button
                style={{ ...S.btn, background: '#b45309', color: '#fff', borderColor: '#b45309' }}
                disabled={busyId === `c${c.id}` || c.kybStatus === 'APPROVED'}
                onClick={() => forceApprove(c)}
                title="Override: approve even without verified Stripe Connect / license / insurance. Recorded in the audit trail (compliance risk)."
              >
                {busyId === `c${c.id}` ? '…' : 'Force approve'}
              </button>
              <button style={S.btnRed} disabled={busyId === `c${c.id}`} onClick={() => reject(c)}>Reject KYB</button>
              <button
                style={S.btnMiddesk}
                disabled={busyId === `c${c.id}`}
                onClick={() => middesk(c)}
                title="Run Middesk Business Verification (name / TIN / Secretary-of-State / OFAC). Inert until MIDDESK_API_KEY is configured."
              >
                {busyId === `c${c.id}` ? '…' : 'Auto-verify (Middesk)'}
              </button>
              <button
                style={S.btnStripe}
                disabled={busyId === `c${c.id}`}
                onClick={() => stripeKyb(c)}
                title="Verify via Stripe Connect (payouts_enabled = business identity + EIN + sanctions cleared). On a verified result KYB is auto-approved."
              >
                {busyId === `c${c.id}` ? '…' : 'Verify via Stripe'}
              </button>
              <button
                style={ownerBan ? S.btn : S.btnRed}
                disabled={busyId === `u${c.ownerUserId || (owner && owner.userId)}` || !(c.ownerUserId || (owner && owner.userId))}
                onClick={() => toggleSuspend(c)}
                title={ownerBan ? 'Re-enable this owner account' : 'Disable this owner account (isBan)'}
              >
                {ownerBan ? 'Reinstate owner' : 'Suspend owner'}
              </button>
            </div>

            {(c.middeskOverallStatus || c.middeskBusinessId || c.middeskVerifiedAt) ? (
              <div style={S.section}>
                <div style={S.secTitle}>
                  Middesk Business Verification
                  {c.middeskVerifiedAt ? <span style={{ ...S.k, fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}> · last run {fmtDate(c.middeskVerifiedAt)}</span> : null}
                  {c.middeskBusinessId ? <span style={{ ...S.k, fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}> · #{c.middeskBusinessId}</span> : null}
                </div>
                <div style={S.mdGrid}>
                  <div style={S.mdCell}>
                    <div style={S.dirLabel}>Overall</div>
                    <span style={S.mdBadge(c.middeskOverallStatus)}>{c.middeskOverallStatus || '—'}</span>
                  </div>
                  <div style={S.mdCell}>
                    <div style={S.dirLabel}>Name match</div>
                    <span style={S.mdBadge(c.middeskNameStatus)}>{c.middeskNameStatus || '—'}</span>
                  </div>
                  <div style={S.mdCell}>
                    <div style={S.dirLabel}>TIN / IRS</div>
                    <span style={S.mdBadge(c.middeskTinStatus)}>{c.middeskTinStatus || '—'}</span>
                  </div>
                  <div style={S.mdCell}>
                    <div style={S.dirLabel}>Secretary of State</div>
                    <span style={S.mdBadge(c.middeskSosStatus)}>{c.middeskSosStatus || '—'}</span>
                  </div>
                  <div style={S.mdCell}>
                    <div style={S.dirLabel}>OFAC / Watchlist</div>
                    <span style={S.mdBadge(c.middeskWatchlistStatus)}>{c.middeskWatchlistStatus || '—'}</span>
                  </div>
                </div>
              </div>
            ) : null}

            {(c.stripeKybCheckedAt || c.stripeKybVerified != null || c.stripeKybPayoutsEnabled != null) ? (
              <div style={S.section}>
                <div style={S.secTitle}>
                  Stripe Connect Verification
                  {c.stripeKybCheckedAt ? <span style={{ ...S.k, fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}> · last checked {fmtDate(c.stripeKybCheckedAt)}</span> : null}
                </div>
                <div style={S.mdGrid}>
                  <div style={S.mdCell}>
                    <div style={S.dirLabel}>Business verified</div>
                    <span style={S.mdBadge(c.stripeKybVerified ? 'verified' : 'failure')}>
                      {c.stripeKybVerified ? 'VERIFIED' : 'NOT VERIFIED'}
                    </span>
                  </div>
                  <div style={S.mdCell}>
                    <div style={S.dirLabel}>Payouts enabled</div>
                    <span style={S.mdBadge(c.stripeKybPayoutsEnabled ? 'verified' : 'failure')}>
                      {c.stripeKybPayoutsEnabled ? 'YES' : 'NO'}
                    </span>
                  </div>
                </div>
              </div>
            ) : null}

            <div style={S.section}>
              <div style={S.secTitle}>Dirigeant · Company Representative</div>
              <div style={S.dirName}>
                {dir.name.value}
                {dir.name.fb ? <span style={S.fallbackHint}>(from account owner)</span> : null}
              </div>
              <div style={S.dirGrid}>
                <div style={S.dirCell}>
                  <div style={S.dirLabel}>Email</div>
                  <div style={S.dirValue}>
                    {dir.email.value}
                    {dir.email.fb ? <span style={S.fallbackHint}>(from account owner)</span> : null}
                  </div>
                </div>
                <div style={S.dirCell}>
                  <div style={S.dirLabel}>Phone(s)</div>
                  <div style={S.dirValue}>
                    {dirPhoneValue}
                    {dirPhoneFb ? <span style={S.fallbackHint}>(from account owner)</span> : null}
                  </div>
                </div>
                <div style={S.dirCell}>
                  <div style={S.dirLabel}>State</div>
                  <div style={S.dirValue}>
                    {dir.state.value}
                    {dir.state.fb ? <span style={S.fallbackHint}>(from account owner)</span> : null}
                  </div>
                </div>
                <div style={S.dirCell}>
                  <div style={S.dirLabel}>Zip</div>
                  <div style={S.dirValue}>
                    {dir.zip.value}
                    {dir.zip.fb ? <span style={S.fallbackHint}>(from account owner)</span> : null}
                  </div>
                </div>
                <div style={S.dirCell}>
                  <div style={S.dirLabel}>City</div>
                  <div style={S.dirValue}>
                    {dir.city.value}
                    {dir.city.fb ? <span style={S.fallbackHint}>(from account owner)</span> : null}
                  </div>
                </div>
              </div>
            </div>

            <div style={S.section}>
              <div style={S.secTitle}>Licenses</div>
              {licenses.length === 0 ? <div style={S.none}>No licenses on file.</div> : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Type</th><th style={S.th}>Number</th><th style={S.th}>State</th>
                      <th style={S.th}>Classification</th><th style={S.th}>Issuing body</th>
                      <th style={S.th}>Expires</th><th style={S.th}>Status</th><th style={S.th}>Doc</th><th style={S.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {licenses.map((l) => (
                      <tr key={`l${l.id}`}>
                        <td style={S.td}>{l.type || '—'}</td>
                        <td style={S.td}>{l.number || '—'}</td>
                        <td style={S.td}>{l.state || '—'}</td>
                        <td style={S.td}>{l.classification || '—'}</td>
                        <td style={S.td}>{l.issuingBody || '—'}</td>
                        <td style={S.td}>{fmtDate(l.expirationDate)}</td>
                        <td style={S.td}><span style={S.badge(l.verificationStatus)}>{l.verificationStatus || 'PENDING'}</span></td>
                        <td style={S.td}>{l.documentUrl ? <a href={l.documentUrl} target="_blank" rel="noreferrer">View</a> : '—'}</td>
                        <td style={S.td}>
                          <div style={S.miniActions}>
                            <button style={S.btnSm} disabled={busyId === `l${l.id}`} onClick={() => verifyLic(l, 'VERIFIED')}>Verify</button>
                            <button style={S.btnRedSm} disabled={busyId === `l${l.id}`} onClick={() => verifyLic(l, 'REJECTED')}>Reject</button>
                            <button style={S.btnGhostSm} disabled={busyId === `l${l.id}`} onClick={() => verifyLic(l, 'EXPIRED')}>Mark expired</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={S.section}>
              <div style={S.secTitle}>Insurance</div>
              {insurances.length === 0 ? <div style={S.none}>No insurance on file.</div> : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Type</th><th style={S.th}>Carrier</th><th style={S.th}>Policy #</th>
                      <th style={S.th}>Coverage</th><th style={S.th}>Currency</th>
                      <th style={S.th}>Expires</th><th style={S.th}>Status</th><th style={S.th}>COI</th><th style={S.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insurances.map((i) => (
                      <tr key={`i${i.id}`}>
                        <td style={S.td}>{i.type || '—'}</td>
                        <td style={S.td}>{i.carrier || '—'}</td>
                        <td style={S.td}>{i.policyNumber || '—'}</td>
                        <td style={S.td}>{fmtMoney(i.coverageAmount)}</td>
                        <td style={S.td}>{i.currency || '—'}</td>
                        <td style={S.td}>{fmtDate(i.expirationDate)}</td>
                        <td style={S.td}><span style={S.badge(i.verificationStatus)}>{i.verificationStatus || 'PENDING'}</span></td>
                        <td style={S.td}>{i.coiDocumentUrl ? <a href={i.coiDocumentUrl} target="_blank" rel="noreferrer">View</a> : '—'}</td>
                        <td style={S.td}>
                          <div style={S.miniActions}>
                            <button style={S.btnSm} disabled={busyId === `i${i.id}`} onClick={() => verifyIns(i, 'VERIFIED')}>Verify</button>
                            <button style={S.btnRedSm} disabled={busyId === `i${i.id}`} onClick={() => verifyIns(i, 'REJECTED')}>Reject</button>
                            <button style={S.btnGhostSm} disabled={busyId === `i${i.id}`} onClick={() => verifyIns(i, 'EXPIRED')}>Mark expired</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ManageCompany;
